import { SimNaoEnum } from 'components/form'
import { MChatPerguntaEnum, MChatRfPerguntaRespostaInput } from 'graphql/types.generated'
import { emptyArrayOf } from 'util/array'
import { isEmpty, isUndefinedOrNull } from 'util/checks'

import { MChatQueryModel } from '../mchat/model-mchat'
import { MchatRfAplicacaoQueryModel } from '../model-mChatRf'
import { getAplicacaoStats } from './consulta-seguimento-aplicacao/utils-consultaSeguimentoAplicacao'
import { CONSULTA_SEGUIMENTO_PERGUNTAS } from './consultaSeguimentoPerguntasMap'
import { PassouFalhouEnum, PodeNaoPodeEnum } from './enums-consultaSeguimento'
import {
  ConsultaSeguimentoFieldModel,
  ConsultaSeguimentoViewModel,
  MChatPerguntaPrincipal,
} from './model-consultaSeguimento'
import { getPerguntaPaiBySubPergunta } from './utils-consultaSeguimento'

export const convertConsultaSeguimentoToInput = (
  consultaSeguimento: ConsultaSeguimentoFieldModel
): ReadonlyArray<MChatRfPerguntaRespostaInput> | null => {
  if (isEmpty(consultaSeguimento)) return null
  return Object.entries(consultaSeguimento.aplicacao).flatMap(([pergunta, resposta]) => {
    const { convertToInput } = CONSULTA_SEGUIMENTO_PERGUNTAS[pergunta as MChatPerguntaPrincipal]
    return convertToInput(resposta)
  })
}

export const convertConsultaSeguimentoQueryToViewModel = (
  consultaSeguimento: MChatQueryModel | undefined | null
): ConsultaSeguimentoViewModel | null => {
  if (!consultaSeguimento || isEmpty(consultaSeguimento)) return null

  const perguntasPai = new Set<MChatPerguntaEnum>()
  const subPerguntas = consultaSeguimento.aplicacao.map((pergunta) => pergunta.pergunta)

  subPerguntas.forEach((subPergunta) => {
    const perguntaPai = getPerguntaPaiBySubPergunta(subPergunta)

    if (perguntaPai && !perguntasPai.has(perguntaPai)) {
      perguntasPai.add(perguntaPai)
    }
  })

  const totalPerguntasRespondidas = perguntasPai.size
  const totalPerguntasFalhou = consultaSeguimento.pontuacao
  const totalPerguntasPassou = totalPerguntasRespondidas - totalPerguntasFalhou

  return {
    resultado: consultaSeguimento.risco,
    totalPerguntasFalhou,
    totalPerguntasPassou,
    totalPerguntasRespondidas,
    footerInfo: {
      dataResultado: consultaSeguimento?.dataResultado,
      lotacao: consultaSeguimento?.atendimentoProfissional?.lotacao,
      atendimentoProfissionalId: consultaSeguimento?.atendimentoProfissional?.id,
    },
    aplicacao: convertAplicacaoQueryToPerguntaRespostaInput(consultaSeguimento.aplicacao),
  }
}

export const convertConsultaSeguimentoFieldToViewModel = (
  consultaSeguimento: ConsultaSeguimentoFieldModel | undefined
): ConsultaSeguimentoViewModel | null => {
  if (!consultaSeguimento || isEmpty(consultaSeguimento)) return null

  const { totalPerguntasFalhou, totalPerguntasPassou, totalPerguntasRespondidas } = getAplicacaoStats(
    consultaSeguimento.aplicacao
  )

  const aplicacao = convertConsultaSeguimentoToInput(consultaSeguimento) ?? emptyArrayOf<MChatRfPerguntaRespostaInput>()

  return {
    resultado: consultaSeguimento.resultado,
    totalPerguntasFalhou,
    totalPerguntasPassou,
    totalPerguntasRespondidas,
    aplicacao,
  }
}

export const convertSimNaoEnumToBoolean = (value: SimNaoEnum | undefined): boolean | null => {
  if (isUndefinedOrNull(value)) return null
  return value === SimNaoEnum.SIM
}

export const convertPodeNaoPodeEnumToBoolean = (value: PodeNaoPodeEnum | undefined): boolean | null => {
  if (isUndefinedOrNull(value)) return null
  return value === PodeNaoPodeEnum.PODE
}

export const convertPassouFalhouEnumToBoolean = (value: PassouFalhouEnum | undefined): boolean | null => {
  if (isUndefinedOrNull(value)) return null
  return value === PassouFalhouEnum.PASSOU
}

export function convertAplicacaoQueryToPerguntaRespostaInput(
  items: ReadonlyArray<MchatRfAplicacaoQueryModel>
): ReadonlyArray<MChatRfPerguntaRespostaInput> {
  return items.map((item) => ({
    pergunta: item.pergunta,
    outros: item.outros,
    perguntaOpcao: item.perguntaOpcao,
    stResposta: item.stPassou,
  }))
}
